import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { CreditApplication, CreditApplicationStatus } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_BASIC, TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { useApolloQueryErrorHandling } from '@sputnik/apollo/useApolloQueryErrorHandling'
import { useAuth } from '@sputnik/auth'
import ImageWrapper from '@sputnik/components/ImageWrapper/ImageWrapper'
import { InviteCollaboratorsDrawer } from '@sputnik/components/InviteCollaborators'
import StatusButtons from '@sputnik/components/StatusButtons'
import StatusTile from '@sputnik/pages/Dashboard/StatusTile/StatusTile'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { matchEmail } from '@sputnik/utils/dataUtils'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useAmendSentToSignerApp } from './AmendSentToSignerApp.gql'
import { useGetApplicationStatusLazyQuery } from './GetApplicationStatus.gql'
import { useRequestSignature } from './RequestSignature.gql'
interface AwaitingSignatureStatusTileProps {
  creditApp: CreditApplication
  refetch: () => void
}

/* shows for AWAITING_SIGNATURE status and for both isAuthSigner and !isAuthSigner output */
const AwaitingSignatureStatusTile = ({ creditApp, refetch }: AwaitingSignatureStatusTileProps) => {
  const [isShowingInviteCollaborators, setIsShowingInviteCollaborators] = useState(false)
  const { email: loginEmail } = useAuth()

  const { authorizedSigner, salesforceCreditId, lastUpdatedByApplicantAt, status } = creditApp || {}
  const isInProgressStatus = status === CreditApplicationStatus.IN_PROGRESS
  const isAwaitingSignatureStatus = status === CreditApplicationStatus.AWAITING_SIGNATURE
  const isAuthSigner = matchEmail(authorizedSigner?.email, loginEmail)
  const showDangerToast = useToast(TOAST_TYPE_DANGER) as any
  const showBasicToast = useToast(TOAST_TYPE_BASIC) as any
  const showSuccessToast = useToast(TOAST_TYPE_SUCCESS) as any
  const navigate = useNavigate()

  const [makeTestId] = useNameFormatter('AwaitingSignatureStatusTile')
  const [getAppStatus, { loading: getStatusLoading, error }] = useGetApplicationStatusLazyQuery({
    fetchPolicy: 'no-cache',
    variables: {
      creditApplicationId: salesforceCreditId,
    },
  })

  useApolloQueryErrorHandling({
    error,
    toast: {
      message: (
        <FormattedMessage
          defaultMessage='Error updating credit application status.'
          id='sputnik.AwaitingSignatureStatusTile__zbtJ07'
        />
      ),
      options: {
        autoHideDelay: 5000,
        dataTestId: 'refetch-status-application-fail',
      },
    },
  })

  const [amendSentToSignerApp, { loading: isLoadingAmendSentToSigner }] = useAmendSentToSignerApp({
    onCompleted: () => {
      logInfo('amendApplication', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: 'Application amended',
        },
        eventName: 'amendSentToSignerApp',
      })

      navigate(`/app/${salesforceCreditId}/${PATHNAME_CONSTS.REVIEW_PATH}`)
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          action: 'amendApplication',
          creditAppId: salesforceCreditId,
          result: 'Application amend failed',
          message: error?.message,
        },
        eventName: 'amendSentToSignerApp',
      })

      showDangerToast(
        <FormattedMessage
          defaultMessage='Error amending credit application.'
          id='sputnik.AwaitingSignatureStatusTile__kBwmHX'
        />,
        {
          autoHideDelay: 5000,
          dataTestId: 'clone-application-fail',
        },
      )
    },
    variables: { salesforceCreditId },
  })

  const [requestSignature, { loading: isLoadingRequestSignature }] = useRequestSignature({
    variables: {
      creditApplicationId: salesforceCreditId,
    },
    onCompleted: () => {
      logInfo('resendToSigner', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: 'Successfully resent the credit app to signer',
        },
        eventName: 'resendToSignerSuccess',
      })

      showSuccessToast(
        <FormattedMessage
          defaultMessage='Successfully resent the credit application to authorized signer'
          id='sputnik.AwaitingSignatureStatusTile__dW11Qw'
        />,
        {
          autoHideDelay: 5000,
        },
      )
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          action: 'Resend to Signer',
          creditAppId: salesforceCreditId,
          result: 'Failed to resend application to signer',
          message: error?.message,
        },
        eventName: 'resendToSignerError',
      })

      showDangerToast(
        <FormattedMessage
          defaultMessage='Error resending application for signature.'
          id='sputnik.AwaitingSignatureStatusTile__dA/cyp'
        />,
        {
          autoHideDelay: 5000,
        },
      )
    },
  })

  const onAmendApplicationClick = async () => {
    const newStatus = await getAppStatus()
    if (newStatus?.data?.creditApplication?.status === CreditApplicationStatus.AWAITING_SIGNATURE) {
      amendSentToSignerApp()
    } else {
      showBasicToast(
        <FormattedMessage
          defaultMessage='Looks like your credit application status has changed. Updating dashboard to reflect latest changes.'
          id='sputnik.AwaitingSignatureStatusTile__CqVMav'
        />,
        {
          autoHideDelay: 5000,
          dataTestId: 'refetch-application-status',
        },
      )
      refetch()
    }
  }

  const onResendToSignerClick = async () => {
    if (status === CreditApplicationStatus.AWAITING_SIGNATURE) {
      requestSignature()
    } else {
      showBasicToast(
        <FormattedMessage
          defaultMessage='Looks like your credit application status has changed. Updating dashboard to reflect latest changes.'
          id='sputnik.AwaitingSignatureStatusTile__CqVMav'
        />,
        {
          autoHideDelay: 5000,
        },
      )
      refetch()
    }
  }

  if ((isAuthSigner && isAwaitingSignatureStatus) || isInProgressStatus) {
    return (
      <div data-testid={makeTestId('is-auth-signer')}>
        <StatusTile
          creditApp={creditApp}
          image={
            <ImageWrapper
              alt=''
              imageName='dashboard-application-in-progress-status'
            />
          }
          primaryInfo={
            <FormattedMessage
              defaultMessage='You are almost there!'
              id='sputnik.AwaitingSignatureStatusTile__WMHqts'
            />
          }
          secondaryInfo={
            <FormattedMessage
              defaultMessage='Last updated on {date, date, long}'
              id='sputnik.AwaitingSignatureStatusTile__nqBLM8'
              values={{ date: new Date(lastUpdatedByApplicantAt) }}
            />
          }
          title={
            <FormattedMessage
              defaultMessage='Resume application'
              id='sputnik.AwaitingSignatureStatusTile__uGKLWh'
            />
          }
        >
          <StatusButtons
            primaryPath={`/app/${salesforceCreditId}/${PATHNAME_CONSTS.NAME_AND_OWNERSHIP_PATH}`}
            primaryText={
              <FormattedMessage
                defaultMessage='Resume application'
                id='sputnik.AwaitingSignatureStatusTile__uGKLWh'
              />
            }
            secondaryText={
              <FormattedMessage
                defaultMessage='Invite collaborators'
                id='sputnik.AwaitingSignatureStatusTile__ni18Y2'
              />
            }
            onSecondaryClick={() => setIsShowingInviteCollaborators(true)}
          />
          <InviteCollaboratorsDrawer
            appId={salesforceCreditId}
            isShowing={isShowingInviteCollaborators}
            onClose={() => setIsShowingInviteCollaborators(false)}
          />
        </StatusTile>
      </div>
    )
  }

  // if !isAuthSigner
  return (
    <div data-testid={makeTestId('is-not-auth-signer')}>
      <StatusTile
        creditApp={creditApp}
        image={
          <ImageWrapper
            alt=''
            imageName='dashboard-application-correction-status'
          />
        }
        primaryInfo={
          <FormattedMessage
            defaultMessage='We are waiting on {asName} to sign and submit your application.'
            id='sputnik.AwaitingSignatureStatusTile__xssD35'
            values={{ asName: `${authorizedSigner?.firstName} ${authorizedSigner?.lastName}` }}
          />
        }
        secondaryInfo={
          <FormattedMessage
            defaultMessage='Application sent on {date, date, long}'
            id='sputnik.AwaitingSignatureStatusTile__YjNv7z'
            values={{ date: new Date(lastUpdatedByApplicantAt) }}
          />
        }
        title={
          <FormattedMessage
            defaultMessage='Sent to signer'
            id='sputnik.AwaitingSignatureStatusTile__shdH4T'
          />
        }
      >
        <StatusButtons
          primaryLoading={isLoadingRequestSignature}
          primaryText={
            <FormattedMessage
              defaultMessage='Re-send application'
              id='sputnik.AwaitingSignatureStatusTile__HDL1Hu'
            />
          }
          secondaryLoading={isLoadingAmendSentToSigner || getStatusLoading}
          secondaryText={
            <FormattedMessage
              defaultMessage='Amend application'
              id='sputnik.AwaitingSignatureStatusTile__BB1eP1'
            />
          }
          onPrimaryClick={onResendToSignerClick}
          onSecondaryClick={onAmendApplicationClick}
        />
      </StatusTile>
    </div>
  )
}

export default AwaitingSignatureStatusTile
